// Wave AW.3-React (2026-05-16) — 외부 바이어 웹 로그인 (auction.autodex.kr)
//
// ERP Login.js 디자인 시스템 정합:
//   - Pretendard 폰트 + linear-gradient 배경 + blur 원 + 흰 카드
//   - inline styles (CLAUDE.md var 룰)
//
// 백엔드: POST /buyer-app/auth/login-web (email + password, device_id 면제)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { LogoFull } from '../Logo';
import { useT } from '../../i18n/I18nProvider';
import { displayApiError } from './buyerApi';
import TermsModal from './TermsModal';
import DevicePolicyBanner from './DevicePolicyBanner';

function AuctionBuyerLogin() {
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);
  var [showPw, setShowPw] = useState(false);
  // Wave UX-2.4 #27 — 세션 만료 안내 (handleUnauthorized 로 진입한 경우 1회 노출)
  var [sessionExpired, setSessionExpired] = useState(false);
  // Wave UX-2.4 #24 — 약관/개인정보 모달
  var [termsTab, setTermsTab] = useState(null);
  var navigate = useNavigate();
  var t = useT();

  useEffect(function() {
    try {
      if (sessionStorage.getItem('autodex_buyer_session_expired') === '1') {
        setSessionExpired(true);
        sessionStorage.removeItem('autodex_buyer_session_expired');
      }
    } catch (e) { /* silent */ }
  }, []);

  var canSubmit = email.indexOf('@') > 0 && password.length >= 8;

  // UX — 버튼 비활성 사유 안내 (회원가입 페이지와 동일 패턴).
  // canSubmit 조건과 1:1 정합 — 회색 버튼이 왜 안 눌리는지 즉시 표시.
  // 흔한 함정: 이메일 대신 ERP 아이디 입력(@ 없음) → missingEmail 노출.
  var missingHints = [];
  if (email.indexOf('@') <= 0) missingHints.push(t('missingEmail'));
  if (password.length < 8) missingHints.push(t('missingPassword'));

  var handleLogin = function() {
    setError('');
    if (!email) { setError(t('errEmailRequired')); return; }
    if (password.length < 8) { setError(t('errPasswordTooShort')); return; }
    setLoading(true);
    fetch(API + '/buyer-app/auth/login-web', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password: password }),
    })
      .then(function(r) { return r.json().then(function(d) { return { status: r.status, body: d }; }); })
      .then(function(res) {
        setLoading(false);
        if (res.body && res.body.access_token) {
          localStorage.setItem('autodex_buyer_token', res.body.access_token);
          localStorage.setItem('autodex_buyer_refresh', res.body.refresh_token || '');
          if (res.body.buyer) {
            sessionStorage.setItem('autodex_buyer', JSON.stringify(res.body.buyer));
          }
          // 로그인 후 진입 — 임시로 같은 페이지 유지 (경매 목록 라우트는 후속 wave)
          navigate('/buyer/dashboard');
          return;
        }
        var code = (res.body && (res.body.code || res.body.error)) || '';
        if (res.status === 403 && /pending/i.test(code)) {
          setError(t('errPendingApproval'));
        } else if (res.status === 403 && /rejected/i.test(code)) {
          setError(t('errRejected'));
        } else if (res.status === 403 && /suspended/i.test(code)) {
          setError(t('errSuspended'));
        } else if (res.status === 401) {
          setError(t('errInvalidCredentials'));
        } else {
          setError(displayApiError(res.body, t, 'errLoginFailed'));
        }
      })
      .catch(function() { setLoading(false); setError(t('errServerConnect')); });
  };

  var inputStyle = {
    width: '100%', height: 48, paddingLeft: 44, paddingRight: 44,
    border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 15,
    outline: 'none', transition: 'border-color 0.2s',
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
  };
  var iconStyle = {
    position: 'absolute', left: 14, top: '50%',
    transform: 'translateY(-50%)', color: '#94a3b8',
  };

  return (
    <div className="buyer-root" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg,#f8fafc 0%,#eff6ff 100%)',
      padding: 20,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: '#dbeafe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -200, left: -200, width: 500, height: 500, background: '#e0f2fe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />
      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 10 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <LogoFull iconSize={40} fontSize={28} />
          <p style={{ color: '#94a3b8', fontSize: 14, fontWeight: 500, letterSpacing: '0.05em', marginTop: 12 }}>USED CAR AUCTION</p>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: 20, padding: '40px 36px', boxShadow: '0 4px 40px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{t('loginTitle')}</h2>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 24 }}>{t('loginSubtitle')}</p>

          {/* Wave UX-2.4 #26 — 1기기 정책 안내 (env flag 활성 시만 자동 노출) */}
          <DevicePolicyBanner />

          {/* Wave UX-2.4 #27 — 세션 만료 정보성 배너 (error 와 분리, 황색 톤) */}
          {sessionExpired && !error && (
            <div style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: '#a16207', fontSize: 18, lineHeight: 1.2 }}>⏰</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#854d0e', fontSize: 13, fontWeight: 700 }}>{t('loginSessionExpiredTitle')}</div>
                <div style={{ color: '#a16207', fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{t('loginSessionExpiredBody')}</div>
              </div>
            </div>
          )}

          {error && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#dc2626', fontSize: 18 }}>⚠</span>
              <span style={{ color: '#991b1b', fontSize: 14, fontWeight: 500 }}>{error}</span>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>{t('loginEmailLabel')}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </span>
              <input type="email" value={email} onChange={function(e){setEmail(e.target.value)}}
                onKeyDown={function(e){if(e.key==='Enter'&&canSubmit)handleLogin()}}
                placeholder="buyer@example.com"
                dir="ltr"
                autoComplete="email"
                style={inputStyle}
                onFocus={function(e){e.target.style.borderColor='#3b82f6'}}
                onBlur={function(e){e.target.style.borderColor='#e2e8f0'}}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>{t('loginPasswordLabel')}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </span>
              <input type={showPw?'text':'password'} value={password} onChange={function(e){setPassword(e.target.value)}}
                onKeyDown={function(e){if(e.key==='Enter'&&canSubmit)handleLogin()}}
                placeholder={t('loginPasswordHint')}
                style={inputStyle}
                onFocus={function(e){e.target.style.borderColor='#3b82f6'}}
                onBlur={function(e){e.target.style.borderColor='#e2e8f0'}}
              />
              <button type="button" onClick={function(){setShowPw(!showPw)}}
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                {showPw ? t('loginPasswordHide') : t('loginPasswordShow')}
              </button>
            </div>
          </div>

          <button type="button" onClick={handleLogin} disabled={!canSubmit||loading}
            style={{
              width: '100%', height: 48,
              background: (!canSubmit||loading) ? '#cbd5e1' : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', border: 'none', borderRadius: 12,
              fontSize: 15, fontWeight: 700, cursor: (!canSubmit||loading)?'not-allowed':'pointer',              transition: 'all 0.2s',
              boxShadow: (!canSubmit||loading) ? 'none' : '0 4px 12px rgba(27,58,92,0.2)',
            }}>
            {loading ? t('loginCtaLoading') : t('loginCta')}
          </button>

          {/* 버튼 비활성 사유 안내 — 회원가입과 동일 패턴 (회색 버튼 침묵 방지) */}
          {!canSubmit && !loading && missingHints.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12, color: '#92400e' }}>
              <span style={{ fontWeight: 600 }}>{t('registerMissingFields')}</span>
              {missingHints.join(', ')}
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{t('loginNoAccount')} </span>
            <button type="button" onClick={function(){navigate('/buyer/register')}}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 700, }}>
              {t('loginRegisterCta')}
            </button>
          </div>
        </div>
        {/* Wave UX-2.4 #24 — 약관/개인정보 동의 안내 + 클릭 (모달) */}
        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 20, lineHeight: 1.6, padding: '0 8px' }}>
          {t('loginTermsAgree')}
          {' '}
          <button type="button" onClick={function(){ setTermsTab('terms'); }}
            style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', textDecoration: 'underline', fontSize: 12, cursor: 'pointer', }}>
            {t('loginTermsLink')}
          </button>
          {' · '}
          <button type="button" onClick={function(){ setTermsTab('privacy'); }}
            style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', textDecoration: 'underline', fontSize: 12, cursor: 'pointer', }}>
            {t('loginPrivacyLink')}
          </button>
        </p>
        <p style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11, marginTop: 14 }}>
          © 2026 AUTODEX. All rights reserved.
        </p>
      </div>
      {termsTab && (
        <TermsModal initialTab={termsTab} onClose={function(){ setTermsTab(null); }} />
      )}
    </div>
  );
}

export default AuctionBuyerLogin;
